import { Consts } from './index';
import { PropertyHolder } from './PropertyHolder';

export interface IProgramQuery {
  channelId: string;
  from: number;
  to: number;
  lang: string;
  onComplete: Function;
  onError: Function;
}

export interface IProgramQueryTime {
  from: number;
  to: number;
}

export enum RequestedProgramType {
  ONE_HOUR,
  SIXTEEN_HOURS,
  EXTENDED,
  CUSTOM_START_FOR_CURRENT_CHANNEL,
}

export class EpgProgramRequestQueue {
  private _programsQueryQueue: IProgramQuery[] = [];
  private _programLoadingFunction: Function;

  constructor(programLoadingFunction: Function) {
    this._programLoadingFunction = programLoadingFunction;
  }

  public add(target: IProgramQuery): void {
    if (!this._programsQueryQueue.length) {
      this._programsQueryQueue.push(target);
    } else {
      const find: IProgramQuery | undefined = this._programsQueryQueue.find(({ channelId}) => channelId === target.channelId);
      if (find) {
        find.from = Math.min(find.from, target.from);
        find.to = Math.max(find.to, target.to);
        find.onComplete = target.onComplete;
        find.onError = target.onError;
      } else {
        this._programsQueryQueue.push(target);
      }
    }
  }

  public create(
    channelsId: number[],
    onComplete: Function,
    onError: Function,
    requestedProgramType: RequestedProgramType,
    startTimestamp: number,
    timeOffset: number,
    lang: string,
  ): void {
    const targetTime = this.calculateTimeFromType(
      requestedProgramType,
      startTimestamp,
      timeOffset,
    );
    const query: IProgramQuery = {
      channelId: channelsId.join(','),
      from: targetTime.from,
      to: targetTime.to,
      lang,
      onComplete,
      onError,
    };

    this.add(query);
  }

  public onTimeUpdate(): void {
    if (!this._programsQueryQueue.length) {
      return;
    }
    const query: IProgramQuery | undefined = this._programsQueryQueue.shift();
    if (!query) {
      return;
    }

    const queryString: string = `?channel_id=${query.channelId}&locale=${query.lang}&from=${query.from}&to=${query.to}`;
    this._programLoadingFunction(queryString, query.onComplete, query.onError);
  }

  private calculateTimeFromType(
    type: RequestedProgramType,
    timestamp: number,
    offset: number,
  ): IProgramQueryTime {
    let time: IProgramQueryTime;
    switch (type) {
      case RequestedProgramType.EXTENDED:
        time = this.calculateTimeFromExtended(offset);
        break;
      case RequestedProgramType.CUSTOM_START_FOR_CURRENT_CHANNEL:
      case RequestedProgramType.SIXTEEN_HOURS:
        time = this.calculateTimeFromSixtenHours(timestamp);
        break;
      case RequestedProgramType.ONE_HOUR:
        time = { from: timestamp, to: timestamp + 3600 };
        break;
      default:
        time = { from: timestamp, to: timestamp };
        break;
    }

    return time;
  }

  private calculateTimeFromExtended(timeOffset: number): IProgramQueryTime {
    const millisPerDay = 86400000;
    const nowDate = new Date();
    const millisToDayStart =
      nowDate.getHours() * 3600000 +
      nowDate.getMinutes() * 60000 +
      nowDate.getSeconds() * 1000 +
      nowDate.getMilliseconds();
    const startCurrentDayTimestamp = nowDate.getTime() - millisToDayStart;

    return {
      from:
        new Date(startCurrentDayTimestamp - millisPerDay * 14).getTime() /
          1000 +
        timeOffset,
      to:
        new Date(startCurrentDayTimestamp + millisPerDay * 7).getTime() / 1000 +
        timeOffset,
    };
  }

  private calculateTimeFromSixtenHours(
    startTimestamp: number,
  ): IProgramQueryTime {
    const eightHours: number = 28800;
    const threeHours: number = 10800;

    const currentTimeStamp: number = PropertyHolder.get(
      Consts.PROPERTY_CURRENT_TIMESTAMP,
    );
    let start = currentTimeStamp - eightHours;
    if (startTimestamp && startTimestamp <= start) {
      start = startTimestamp - threeHours;
    }

    return {
      from: start,
      to: currentTimeStamp + eightHours,
    };
  }
}
